// All editorial content for the ENT website (Local SEO + GEO structured).
import {
  Ear,
  Wind,
  Stethoscope,
  Activity,
  Baby,
  ShieldPlus,
  Waves,
  Droplets,
  Volume2,
} from "lucide-react";

export const CONDITIONS = [
  {
    icon: Ear,
    title: "Ear Pain & Infections",
    group: "Ear",
    desc: "Diagnosis and treatment of acute and chronic ear infections, earache and discharge in adults and children.",
    span: "md:col-span-5",
  },
  {
    icon: Volume2,
    title: "Hearing Loss & Tinnitus",
    group: "Ear",
    desc: "Audiometry-based evaluation of hearing loss and ringing in the ears, with medical or surgical management.",
    span: "md:col-span-4",
  },
  {
    icon: Waves,
    title: "Vertigo & Balance",
    group: "Ear",
    desc: "Structured assessment and rehabilitation for dizziness, BPPV and balance disorders.",
    span: "md:col-span-3",
  },
  {
    icon: Wind,
    title: "Sinusitis & Nasal Blockage",
    group: "Nose",
    desc: "Endoscopic diagnosis of sinusitis, blocked nose and recurrent nasal infections.",
    span: "md:col-span-3",
  },
  {
    icon: Droplets,
    title: "Nasal Allergies",
    group: "Nose",
    desc: "Allergy evaluation and long-term relief plans for sneezing, congestion and post-nasal drip.",
    span: "md:col-span-4",
  },
  {
    icon: Stethoscope,
    title: "Tonsillitis & Sore Throat",
    group: "Throat",
    desc: "Management of recurrent tonsillitis, throat infections, and difficulty swallowing.",
    span: "md:col-span-5",
  },
  {
    icon: Activity,
    title: "Snoring & Voice Disorders",
    group: "Throat",
    desc: "Evaluation of snoring, sleep-disordered breathing and hoarseness or voice change.",
    span: "md:col-span-6",
  },
  {
    icon: Baby,
    title: "Children's ENT",
    group: "Paediatric",
    desc: "Gentle, child-friendly care for ear infections, adenoids, tonsils and mouth-breathing in kids.",
    span: "md:col-span-6",
  },
];

export const TREATMENTS = [
  {
    title: "Endoscopic Sinus Surgery",
    desc: "Minimally invasive FESS to clear blocked sinuses and restore healthy drainage — no external cuts.",
    tag: "Advanced Nasal Surgery",
  },
  {
    title: "Septoplasty",
    desc: "Corrective surgery for a deviated nasal septum to relieve chronic nasal obstruction and improve breathing.",
    tag: "Nasal Reconstruction",
  },
  {
    title: "Tympanoplasty",
    desc: "Microsurgical repair of a perforated eardrum to stop recurrent discharge and improve hearing.",
    tag: "Microscopic Ear Surgery",
  },
  {
    title: "Tonsillectomy & Adenoidectomy",
    desc: "Modern, low-pain removal of chronically infected tonsils and adenoids in children and adults.",
    tag: "Throat & Paediatric",
  },
  {
    title: "Vertigo Rehabilitation",
    desc: "Targeted repositioning manoeuvres and vestibular therapy for lasting relief from dizziness.",
    tag: "Balance Therapy",
  },
  {
    title: "Microscopic Ear Surgery",
    desc: "Precision surgery for chronic ear disease and cholesteatoma using high-magnification microscopy.",
    tag: "Advanced Ear Care",
  },
  {
    title: "Thyroid Surgery",
    desc: "Expert surgical management of thyroid swellings and nodules with a focus on safety and cosmetic results.",
    tag: "Head & Neck",
  },
];

export const WHY_US = [
  {
    stat: "25+",
    label: "Years of clinical experience",
    desc: "Over two and a half decades diagnosing and treating complex ear, nose, throat, head and neck conditions.",
  },
  {
    stat: "3 Lakh+",
    label: "Patients treated",
    desc: "Trusted by families across Varanasi for honest advice and evidence-based ENT care.",
  },
  {
    stat: "MS (ENT)",
    label: "BHU-trained specialist",
    desc: "MBBS & MS (ENT) from Banaras Hindu University with senior residency at Batra Hospital, Delhi.",
  },
  {
    stat: "Award",
    label: "Recognised expertise",
    desc: "Recipient of the Health Legends and Dainik Jagran Wellness+ Healthcare awards.",
  },
];

export const JOURNEY = [
  {
    n: "01",
    title: "Consultation",
    desc: "A detailed conversation about your symptoms and history, followed by a careful ENT examination.",
  },
  {
    n: "02",
    title: "Diagnosis",
    desc: "Precise diagnosis using nasal endoscopy, audiometry and imaging where needed — explained in plain language.",
  },
  {
    n: "03",
    title: "Treatment",
    desc: "A tailored plan, whether medical management or advanced day-care surgery, designed around your life.",
  },
  {
    n: "04",
    title: "Recovery",
    desc: "Clear aftercare, follow-up reviews and support until you are fully back to your best.",
  },
];

export const HOME_FAQS = [
  {
    q: "Who is the best ENT specialist in Varanasi for ear, nose and throat problems?",
    a: "Dr. Sanjay Teza is an experienced ENT specialist in Varanasi offering comprehensive care for ear, nose, throat, vertigo and children's ENT conditions, backed by 15+ years of clinical experience and advanced surgical expertise.",
  },
  {
    q: "What conditions does Dr. Sanjay Teza treat?",
    a: "Dr. Teza treats ear infections, hearing loss, tinnitus, vertigo, sinusitis, nasal blockage, allergies, tonsillitis, sore throat, snoring, voice disorders and a full range of children's ENT problems.",
  },
  {
    q: "How do I book an appointment with Dr. Sanjay Teza?",
    a: "You can book instantly via the appointment form on this website, or message the clinic directly on WhatsApp. Walk-in consultations are also available during clinic hours.",
  },
  {
    q: "Does the clinic offer advanced ENT surgery in Varanasi?",
    a: "Yes. The clinic provides advanced procedures including endoscopic sinus surgery, septoplasty, tympanoplasty and microscopic ear surgery, many performed as day-care procedures.",
  },
  {
    q: "Where is Dr. Sanjay Teza's ENT clinic located?",
    a: "The clinic is located in Sigra, Varanasi, easily accessible from Cantt Railway Station, Rathyatra and BHU. Full address and directions are on the Contact page.",
  },
];

// ---- 25 FAQs for the dedicated FAQ page ----
export const FAQ_CATEGORIES = ["All", "Ear", "Nose", "Throat", "Vertigo", "Children's ENT", "General"];

export const ALL_FAQS = [
  // EAR
  { cat: "Ear", q: "What causes sudden ear pain and how is it treated?", a: "Ear pain is commonly caused by infection, wax impaction, or pressure changes. Treatment depends on the cause — infections may need medication, while wax is safely removed in the clinic. Persistent or severe ear pain should always be examined by an ENT specialist." },
  { cat: "Ear", q: "How do I know if I have hearing loss?", a: "Early signs include asking people to repeat themselves, turning up the TV volume, and struggling to follow conversations in noisy places. A simple, painless audiometry test can accurately measure the type and degree of hearing loss." },
  { cat: "Ear", q: "Is tinnitus (ringing in the ears) curable?", a: "Tinnitus is a symptom rather than a disease. Many cases improve once the underlying cause — such as wax, infection, or noise exposure — is treated. For persistent tinnitus, sound therapy and management strategies can significantly reduce its impact." },
  { cat: "Ear", q: "How is a perforated eardrum treated?", a: "Small perforations often heal on their own with care to keep the ear dry. Larger or non-healing perforations may need tympanoplasty, a microsurgical repair that stops recurrent discharge and can improve hearing." },
  { cat: "Ear", q: "Is it safe to clean ears with cotton buds?", a: "No. Cotton buds push wax deeper and can damage the eardrum. Ears are usually self-cleaning; if wax builds up, it should be removed safely by an ENT specialist rather than at home." },

  // NOSE
  { cat: "Nose", q: "What is the difference between a cold and sinusitis?", a: "A cold usually clears within 7–10 days. Sinusitis is inflammation of the sinuses that lasts longer, often with facial pressure, thick nasal discharge, and reduced smell. Chronic sinusitis (over 12 weeks) needs ENT evaluation, often with nasal endoscopy." },
  { cat: "Nose", q: "Why is my nose always blocked on one side?", a: "Persistent one-sided blockage can be due to a deviated nasal septum, nasal polyps, or a chronic sinus issue. An endoscopic examination identifies the exact cause and guides whether medication or septoplasty is needed." },
  { cat: "Nose", q: "How can I get long-term relief from nasal allergies?", a: "Beyond avoiding triggers, long-term relief comes from a structured plan — targeted nasal sprays, antihistamines, and in some cases allergy testing. This reduces sneezing, congestion, and post-nasal drip over time." },
  { cat: "Nose", q: "What is endoscopic sinus surgery and is it painful?", a: "Functional Endoscopic Sinus Surgery (FESS) clears blocked sinuses through the nostrils with no external cuts. It is performed under anaesthesia, involves minimal discomfort, and most patients return to normal activity within a few days." },
  { cat: "Nose", q: "When should a deviated septum be corrected surgically?", a: "Surgery (septoplasty) is considered when a deviated septum causes ongoing nasal blockage, recurrent sinus infections, or disturbed sleep that does not improve with medication." },

  // THROAT
  { cat: "Throat", q: "When should tonsils be removed?", a: "Tonsillectomy is advised for frequent throat infections (typically several episodes a year), difficulty swallowing, or breathing problems during sleep. The decision is individualised after assessing frequency and impact on daily life." },
  { cat: "Throat", q: "What causes a persistent sore throat?", a: "A sore throat lasting more than two weeks can be due to chronic infection, acid reflux, allergies, or voice overuse. Persistent symptoms — especially with hoarseness or difficulty swallowing — should be examined by an ENT specialist." },
  { cat: "Throat", q: "Why do I keep losing my voice?", a: "Recurrent hoarseness may result from vocal strain, acid reflux, or vocal cord nodules. A painless examination of the voice box helps identify the cause, followed by voice rest, medical treatment, or therapy as needed." },
  { cat: "Throat", q: "Is snoring a sign of a serious problem?", a: "Loud, chronic snoring — especially with pauses in breathing or daytime tiredness — can indicate obstructive sleep apnoea, which needs proper evaluation. ENT assessment identifies the level of obstruction and the right treatment." },
  { cat: "Throat", q: "How is difficulty swallowing evaluated?", a: "Difficulty swallowing is assessed through history, throat examination, and endoscopy when required. Identifying the cause early — whether infection, reflux, or a structural issue — allows prompt, effective treatment." },

  // VERTIGO
  { cat: "Vertigo", q: "What is the most common cause of vertigo?", a: "The most common cause is BPPV (Benign Paroxysmal Positional Vertigo), where tiny crystals in the inner ear are displaced. It causes brief spinning with head movement and responds very well to simple repositioning manoeuvres." },
  { cat: "Vertigo", q: "How is vertigo diagnosed?", a: "Diagnosis involves specific balance tests, positional manoeuvres, and hearing assessment to distinguish inner-ear causes from other conditions. This ensures treatment is targeted at the true source of the dizziness." },
  { cat: "Vertigo", q: "Can vertigo be cured without medication?", a: "Many cases of positional vertigo are resolved with in-clinic repositioning manoeuvres rather than long-term medication. Vestibular rehabilitation exercises further retrain the balance system for lasting relief." },
  { cat: "Vertigo", q: "When should I worry about dizziness?", a: "Seek prompt evaluation if dizziness is severe, recurrent, or accompanied by hearing loss, severe headache, weakness, or difficulty speaking. These may indicate conditions that need urgent attention." },

  // CHILDREN'S ENT
  { cat: "Children's ENT", q: "Why does my child get frequent ear infections?", a: "Young children have shorter, more horizontal eustachian tubes, making ear infections common. Recurrent infections are evaluated for fluid behind the eardrum and, when needed, treated to protect hearing and speech development." },
  { cat: "Children's ENT", q: "What are adenoids and when do they need removal?", a: "Adenoids are lymph tissue behind the nose. When enlarged, they cause mouth-breathing, snoring, and blocked ears in children. Removal (adenoidectomy) is considered when symptoms are persistent and affect sleep or hearing." },
  { cat: "Children's ENT", q: "Is my child's mouth-breathing a concern?", a: "Habitual mouth-breathing can point to enlarged adenoids or nasal allergies and may affect sleep, dental development, and concentration. A gentle ENT examination identifies the cause and appropriate treatment." },
  { cat: "Children's ENT", q: "How are ENT procedures made comfortable for children?", a: "Children are examined in a calm, reassuring way using child-sized equipment. Procedures, when required, are planned carefully with parents fully informed at every step to minimise anxiety." },

  // GENERAL
  { cat: "General", q: "How often should I get an ENT check-up?", a: "Most people only need an ENT visit when symptoms appear. However, anyone with ongoing ear, nose, or throat issues, recurrent infections, or hearing concerns should have a periodic review to prevent complications." },
  { cat: "General", q: "Do I need a referral to see an ENT specialist?", a: "No referral is required. You can book a consultation directly through the website form or WhatsApp, and walk-in visits are welcome during clinic hours." },
];

// ---- Treatment Guide (long-form) ----
export const GUIDE_SECTIONS = [
  {
    id: "ear-disorders",
    title: "Ear Disorders",
    lead: "Comprehensive care for the outer, middle and inner ear — from infections to hearing restoration.",
    overview:
      "Ear disorders range from simple wax impaction and infections to chronic middle-ear disease and hearing loss. Accurate diagnosis with otoscopy and audiometry ensures the right treatment, protecting both hearing and quality of life.",
    symptoms: ["Ear pain or fullness", "Discharge from the ear", "Reduced or muffled hearing", "Ringing (tinnitus)", "Dizziness or imbalance"],
    causes: ["Bacterial or fungal infection", "Wax impaction", "Perforated eardrum", "Chronic middle-ear disease", "Prolonged noise exposure"],
    diagnosis: ["Otoscopic & microscopic ear examination", "Pure-tone audiometry", "Tympanometry", "Imaging (CT) for chronic disease"],
    options: ["Medical management of infections", "Safe micro-suction wax removal", "Tympanoplasty for perforations", "Microscopic ear surgery for chronic disease"],
    recovery:
      "Most ear procedures are day-care. Patients are advised to keep the ear dry, complete medication, and attend follow-up reviews. Hearing improvement is assessed at scheduled check-ups.",
  },
  {
    id: "nose-sinus",
    title: "Nose & Sinus Disorders",
    lead: "Restoring clear, comfortable breathing through medical and minimally invasive surgical care.",
    overview:
      "Nasal and sinus problems — from allergies and a deviated septum to chronic sinusitis and polyps — affect breathing, sleep and smell. Nasal endoscopy allows precise diagnosis and a targeted treatment plan.",
    symptoms: ["Blocked or runny nose", "Facial pain or pressure", "Reduced sense of smell", "Post-nasal drip", "Recurrent sinus infections"],
    causes: ["Allergic rhinitis", "Deviated nasal septum", "Nasal polyps", "Chronic sinus inflammation", "Structural narrowing"],
    diagnosis: ["Diagnostic nasal endoscopy", "Allergy assessment", "CT scan of the sinuses", "Clinical history review"],
    options: ["Targeted medical & allergy therapy", "Septoplasty for a deviated septum", "Functional Endoscopic Sinus Surgery (FESS)", "Polyp removal"],
    recovery:
      "Endoscopic procedures involve no external cuts and minimal downtime. Saline rinses and follow-up cleaning support healing, with most patients breathing noticeably better within a week or two.",
  },
  {
    id: "throat-voice",
    title: "Throat & Voice Disorders",
    lead: "Care for the tonsils, throat, voice box and swallowing — for adults and children.",
    overview:
      "Throat conditions include recurrent tonsillitis, chronic sore throat, snoring, and voice disorders. Evaluation focuses on relieving symptoms while protecting the voice and airway.",
    symptoms: ["Recurrent sore throat", "Difficulty or pain swallowing", "Persistent hoarseness", "Loud snoring", "Throat clearing & lump sensation"],
    causes: ["Recurrent tonsil infection", "Acid reflux", "Vocal strain or nodules", "Airway obstruction", "Allergies"],
    diagnosis: ["Throat & tonsil examination", "Flexible laryngoscopy", "Sleep & snoring assessment", "Reflux evaluation"],
    options: ["Medical management & voice rest", "Tonsillectomy for recurrent infection", "Reflux & lifestyle therapy", "Snoring / airway procedures"],
    recovery:
      "Throat procedures are typically day-care with a short, guided recovery. Soft diet, hydration, and voice care speed healing, with follow-up to confirm full resolution.",
  },
  {
    id: "advanced-procedures",
    title: "Advanced ENT Procedures",
    lead: "Precision surgery using endoscopy and microscopy for lasting results.",
    overview:
      "Advanced procedures address complex ENT conditions with modern, minimally invasive techniques — reducing pain, scarring and recovery time compared to traditional surgery.",
    symptoms: ["Chronic sinusitis unresponsive to medicine", "Long-standing nasal blockage", "Recurrent ear discharge", "Persistent balance problems"],
    causes: ["Structural abnormalities", "Chronic inflammation", "Recurrent infection", "Inner-ear dysfunction"],
    diagnosis: ["Endoscopic & microscopic assessment", "High-resolution imaging", "Pre-surgical fitness evaluation"],
    options: ["Endoscopic Sinus Surgery (FESS)", "Septoplasty", "Tympanoplasty", "Microscopic ear surgery", "Vertigo rehabilitation"],
    recovery:
      "Detailed pre- and post-operative guidance is provided for every procedure. Most surgeries are day-care, with structured follow-up ensuring a smooth and complete recovery.",
  },
];
